"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCsvArrayChunkReader = void 0;
const papaparse_1 = __importDefault(require("papaparse"));
const error_1 = require("../error");
const finleyBuffer_1 = require("../finleyBuffer");
const buffer_1 = require("./buffer");
const stripCarriageReturn = (str) => str.charAt(str.length - 1) === '\r' ? str.slice(0, str.length - 1) : str;
/**
 * ParseConfig for robust handling of newlines.
 *
 * If replacing `transform` or `transformHeader`, the custom implementation must call
 * `stripCarriageReturn`.
 *
 * Usage example:
 * ```
 * { ...NEWLINE_PARSE_CONFIG, header: true }.
 * ```
 */
const NEWLINE_PARSE_CONFIG = {
    // Papaparse's automatic line-ending detection does not robustly handle files that use line
    // endings inconsistently. We force it to treat \n as the newline character. This may leave an
    // extra \r in the last column of every line, which we then need to strip out.
    newline: '\n',
    transform: stripCarriageReturn,
    transformHeader: stripCarriageReturn,
};
// More Papaparse config options can be exposed if needed (all options https://www.papaparse.com/docs)
/**
 * Converts csv string to array of arrays
 * @param csvString csv in string format. To convert from buffer, use csvBufferToValueArray method instead
 * @returns
 * data: an array of rows. Each row is an array of values;
 * delimiter
 *
 * Sample return.data:
 * [
 *   ['industry', 'exposure', 'date'],
 *   ['Manufacturing', '12345', '2023-01-01'],
 *   ['Technology', '123', '2023-01-01']
 * ]
 */
const csvStringToValueArray = ({ csvString, delimiter, }) => {
    const config = {
        ...NEWLINE_PARSE_CONFIG,
        delimiter: delimiter || '',
        header: false,
        dynamicTyping: false,
    };
    const parsed = papaparse_1.default.parse(csvString, config);
    return {
        data: parsed.data,
        delimiter: delimiter || parsed.meta.delimiter,
    };
};
/**
 * Converts csv string to array of objects
 * @param csvString csv in string format. To convert from buffer, use csvBufferToJsonArray method instead
 * @returns
 * data: an array of rows, each of which is an object keyed by the field name;
 * delimiter
 *
 * Sample return.data:
 * [
 *   {
 *     industry: 'Manufacturing',
 *     exposure: '12345',
 *     date: '2023-01-01'
 *   },
 *   {
 *     industry: 'Technology',
 *     exposure: '123',
 *     date: '2023-01-01'
 *   },
 * ]
 */
const csvStringToJsonArray = ({ csvString, delimiter, }) => {
    const config = {
        ...NEWLINE_PARSE_CONFIG,
        delimiter: delimiter || ',',
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
    };
    const parsed = papaparse_1.default.parse(csvString, config);
    return {
        data: parsed.data,
        delimiter: delimiter || parsed.meta.delimiter,
    };
};
/**
 * Converts csv buffer to array of rows. Each row is an array of objects of data keyed by the field/header name
 * @param csvBuffer csv in buffer format. To convert from csv string, use `csvStringToValueArray` method instead
 * @returns
 * data: an array of rows. Each row is an array of values;
 * delimiter
 */
const csvBufferToValueArray = ({ csvBuffer, delimiter, }) => {
    const csvString = (0, finleyBuffer_1.toString)(csvBuffer);
    return csvStringToValueArray({ csvString, delimiter });
};
/**
 * Converts csv buffer to array of rows. Each row is an array of objects of data keyed by the field/header name
 * @param csvBuffer csv in buffer format. To convert from csv string, use `csvStringToJsonArray` method instead
 * @returns
 * data: an array of rows, each of which is an object keyed by the field name;
 * delimiter
 */
const csvBufferToJsonArray = ({ csvBuffer, delimiter, }) => {
    const csvString = (0, finleyBuffer_1.toString)(csvBuffer);
    return csvStringToJsonArray({ csvString, delimiter });
};
/**
 * Converts an array of rows (values) or an array of objects to a csv string
 * @param header If false, will omit the header row. If data is an array of arrays this option is ignored. If data is an array of objects the keys of the first object are the header row.
 * @returns a properly delimited string
 *
 * Sample csvArray input (array of arrays):
 * [
 *   ['industry', 'exposure', 'date'],
 *   ['Manufacturing', '12345', '2023-01-01'],
 *   ['Technology', '123', '2023-01-01']
 * ]
 *
 * Sample csvArray input (array of objects):
 * [
 *   {
 *     industry: 'Manufacturing',
 *     exposure: '12345',
 *     date: '2023-01-01'
 *   },
 *   {
 *     industry: 'Technology',
 *     exposure: '123',
 *     date: '2023-01-01'
 *   },
 * ]
 *
 * Expected output from either of the above:
 * 'industry,exposure,date\r\nManufacturing,12345,2023-01-01\r\nTechnology,123,2023-01-01'
 */
const arrayToCsvString = ({ csvArray, header, delimiter, newline = '\r\n', quotes = false, }) => {
    return papaparse_1.default.unparse(csvArray, { header, delimiter, newline, quotes });
};
// Split array on some arbitrary fixed number of rows
// 50k seems safe enough to fit in a single string
const DEFAULT_ROW_CHUNK_SIZE = 50000;
/**
 * The chunk reader logic is a pure refactor out of csvArrayToFinleyBuffer (below)
 * and tests exercising that function will cover this one for now (wide variety
 * within universal/common and universal/tape-sanitizer) - clean up tests when
 * consolidating functionality across packages
 *
 * @param csvArray - expects an array of objects, or a value array (array of arrays,
 * i.e. Util.Primitive[][]), representing csv-like data
 */
const getCsvArrayChunkReader = ({ csvArray, delimiter, newline = '\r\n', quotes = false, rowChunkSize = DEFAULT_ROW_CHUNK_SIZE, // Do not override unless explicitly desired
columns, }) => {
    let offset = 0;
    return {
        getChunk: () => {
            // Special condition for first iteration, send empty string prior to returning null
            if (offset === 0 && csvArray.length === 0) {
                offset += rowChunkSize;
                return '';
            }
            // Terminal condition: if offset (index) has reached length (index +1) or greater, all rows have been read
            if (offset >= csvArray.length) {
                return null;
            }
            const nextOffset = Math.min(offset + rowChunkSize, csvArray.length);
            const chunk = csvArray.slice(offset, nextOffset);
            // Convert to csv string with Papa.unparse, which supports converting
            // array of objects and array of arrays
            const csvStringRows = papaparse_1.default.unparse(chunk, {
                delimiter,
                newline,
                quotes,
                columns,
                // for array of objects, only include header for first iteration
                // for array of arrays, this option is ignored per papaparse docs
                header: offset === 0,
            }).split(newline);
            let stringChunk = csvStringRows.join(newline);
            offset = nextOffset;
            if (offset < csvArray.length) {
                stringChunk += newline;
            }
            return stringChunk;
        },
    };
};
exports.getCsvArrayChunkReader = getCsvArrayChunkReader;
/**
 * TODO: @sachinr4 add stream conversion option when it becomes necessary (CSV files at this time will be smaller than max single buffer size)
 * For any file transfer errors that may occur, it is sufficient for now to convert to a readable stream at the edge where we handle outbound transfers
 *
 * @param csvArray - Converts a value array (array of arrays, i.e. Util.Primitive[][]),
 * or an array of objects, representing csv-like data, to a Buffer
 * @returns a Buffer that is derived from the output of the arrayToCsvString method above
 */
const csvArrayToFinleyBuffer = ({ csvArray, delimiter, newline = '\r\n', quotes = false, rowChunkSize = DEFAULT_ROW_CHUNK_SIZE, // Do not override unless explicitly desired
 }) => {
    const buffers = [];
    const reader = (0, exports.getCsvArrayChunkReader)({
        csvArray,
        delimiter,
        newline,
        quotes,
        rowChunkSize,
    });
    let chunk = reader.getChunk();
    // eslint-disable-next-line no-restricted-syntax
    while (chunk !== null) {
        buffers.push((0, finleyBuffer_1.fromString)(chunk));
        chunk = reader.getChunk();
    }
    // combine buffers
    return (0, finleyBuffer_1.combineBuffers)(buffers);
};
/**
 * CSV file splitter that accepts objects representing CSV rows
 * See `splitCSVBuffer` below if operating with external file inputs
 * The logic here will include consideration for header row size when operating with file size units
 * Data row units refers to the number of rows in the file excluding the header
 */
const splitCSV = ({ csv, partSize, }) => {
    const csvs = [];
    // Current list of CSV rows within limit
    let currentCsv = [];
    // Current size of CSV rows after conversion to string
    let currentCsvByteLength = 0;
    for (const row of csv) {
        // Keep header if this is a new csv
        const keepHeader = currentCsv.length === 0;
        switch (partSize.unit) {
            // Size strategy requires conversion of existing row to CSV to measure length
            case 'MB': {
                // Check if inclusion of new line would exceed limit in bytes
                const byteLengthLimit = partSize.value * 1024 * 1024;
                // Convert to string to check size
                const csvString = arrayToCsvString({
                    csvArray: [row],
                    header: keepHeader,
                });
                // Maintain row byte length for use when updating state of current CSV
                // Only updated and relevant for part size units corresponding to file size
                const rowByteLength = Buffer.byteLength(`${csvString}\r\n`);
                // If limit would be exceeded, this row will be moved into a new CSV
                if (currentCsvByteLength + rowByteLength > byteLengthLimit) {
                    // Push buffer version of currentCsvArray to chunks
                    csvs.push(currentCsv);
                    // Adjust current variables (reset CSV and byte length to match new row)
                    currentCsv = [row];
                    currentCsvByteLength = rowByteLength;
                }
                else {
                    currentCsv.push(row);
                    currentCsvByteLength += rowByteLength;
                }
                break;
            }
            // Row count strategy is direct comparison against rows in CSV
            case 'dataRows': {
                // If limit would be exceeded, this row will be moved into a new CSV
                if (currentCsv.length + 1 > partSize.value) {
                    // Push buffer version of currentCsvArray to chunks
                    csvs.push(currentCsv);
                    // Adjust current variables (reset CSV and byte length to match new row)
                    currentCsv = [row];
                }
                else {
                    currentCsv.push(row);
                }
                break;
            }
            default:
                (0, error_1.finleyAssertUnreachable)(partSize.unit);
        }
    }
    // For any final remaining array, convert and add to chunks
    if (currentCsv.length > 0) {
        csvs.push(currentCsv);
    }
    return csvs;
};
/**
 * Split CSV when provided with buffer
 * If only header is present, return a single result with data row count of 0
 */
const splitCSVBuffer = async ({ csvBuffer, partSize, }) => {
    const csvBuffers = [];
    const { csvArray } = await (0, buffer_1.finleyBufferToCsvArray)({
        buffer: csvBuffer,
    });
    // Header only, no rows available
    if (csvArray.length === 0) {
        // Return original input and set row count to 0
        return [{ csvBuffer, dataRowCount: 0 }];
    }
    const splitCsvs = splitCSV({
        csv: csvArray,
        partSize,
    });
    for (const csv of splitCsvs) {
        csvBuffers.push({
            csvBuffer: csvArrayToFinleyBuffer({ csvArray: csv }),
            dataRowCount: csv.length,
        });
    }
    return csvBuffers;
};
exports.default = {
    // TODO: @sachinr4 - deprecate existing options
    NEWLINE_PARSE_CONFIG,
    csvStringToValueArray,
    csvStringToJsonArray,
    csvBufferToValueArray,
    csvBufferToJsonArray,
    arrayToCsvString,
    // Optimized methods for use
    csvArrayToFinleyBuffer,
    finleyBufferToCsvArray: buffer_1.finleyBufferToCsvArray,
    getCsvArrayChunkReader: exports.getCsvArrayChunkReader,
    splitCSV,
    splitCSVBuffer,
};
//# sourceMappingURL=index.js.map